import React from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { calculateSsortOrder } from 'helpers/sortOrderCalculations';
import {
  discount,
  frequencyOptions
} from './helpers';
import { useStyles } from './styles';


const validationSchema = yup.object({
  frequency: yup
    .string()
    .required('Required'),
  percent_discount: yup
    .string()
    .required('Required'),
  editedSubscriptionOption: yup.object({
    sproduct_label: yup
      .string()
      .required('Required')
  })
});

const EditSubscriptionModal = ({
  existFrequency,
  editableSubscription,
  setEditableSubscription,
  handleSaveSubscriptionOption
}) => {
  const classes = useStyles();

  const {
    editableQuoteProduct
  } = useSelector((state: IReduxState) => state.quote);

  const frequencyTitle = `${editableSubscription?.frequency_count} ${editableSubscription?.frequency_unit}${editableSubscription?.frequency_count > 1 ? 's' : ''}`;

  const handleFrequencyChange = (event, setFieldValue, handleChange) => {
    const { value } = event.target;
    const [frequencyCount, frequencyUnit] = value.split(/\s+/);
    const ssortOrder = calculateSsortOrder(frequencyCount, frequencyUnit);

    setFieldValue('editedSubscriptionOption.ssort_order', ssortOrder);
    setFieldValue('editedSubscriptionOption.frequency_unit', frequencyUnit.replace('s', ''));
    setFieldValue('editedSubscriptionOption.frequency_count', frequencyCount);
    handleChange(event);
  };
  return (
    <Dialog
      className={classes.dialog}
      open={Boolean(editableSubscription)}
      onClose={() => setEditableSubscription(null)}
      aria-labelledby="edit-subscription-option"
    >
      <DialogTitle id="edit-subscription-option">Subscription Options:</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Edit options for subscription
        </DialogContentText>
        <Formik
          initialValues={{
            editedSubscriptionOption: {
              id: editableSubscription?.id,
              product_id: editableQuoteProduct?.id,
              frequency_count: editableSubscription?.frequency_count,
              frequency_unit: editableSubscription?.frequency_unit,
              sproduct_label: editableSubscription?.sproduct_label || '',
              installment_price: editableSubscription?.installment_price,
              additional_information: editableSubscription?.additional_information,
              ssort_order: editableSubscription?.ssort_order,
            },
            frequency: editableSubscription
              ? frequencyTitle
              : '',
            percent_discount: editableSubscription
              ? `${editableSubscription.additional_information.length
                ? JSON.parse(`${editableSubscription.additional_information[0]}`).discount_percentage
                : ''}${editableSubscription.frequency_count != 0 ? '%' : ''}`
              : '',
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSaveSubscriptionOption}
        >
          {({
            values,
            errors,
            touched,
            isValid,
            dirty,
            setFieldValue,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldTouched
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid item xs={12}>
                <CardContent className="px-0 flex items-center justify-around">
                  <TextField
                    className="mx-2 min-w-160"
                    name="frequency"
                    select
                    label={'Frequency'}
                    value={values.frequency}
                    variant="outlined"
                    onChange={(event) => handleFrequencyChange(event, setFieldValue, handleChange)}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.frequency && Boolean(errors.frequency)}
                    helperText={touched.frequency && errors.frequency}
                  >
                    {frequencyOptions.map((option, idx) => (
                      <MenuItem
                        key={`option-${idx}`}
                        value={option}
                        className={`${(existFrequency.includes(option) && frequencyTitle !== option) ? 'hidden' : ''}`}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    className="mx-2 min-w-160"
                    name="percent_discount"
                    select
                    label={'Percent discount'}
                    variant="outlined"
                    value={values.percent_discount}
                    onChange={(event) => {
                      const {value} = event.target;

                      if (value) {
                        setFieldValue(
                          'editedSubscriptionOption.installment_price',
                          (editableQuoteProduct?.price - ((editableQuoteProduct?.price / 100) * Number(value.slice(0, -1))))
                        );
                        setFieldValue(
                          'editedSubscriptionOption.additional_information',
                          [{discount_percentage: parseFloat(value)}]
                        );
                      }

                      handleChange(event);
                    }}
                    onBlur={() => {
                      setFieldTouched('editedSubscriptionOption.percent_discount', true);
                    }}
                    inputProps={{ className: 'text-center' }}
                    error={touched.percent_discount && Boolean(errors.percent_discount)}
                    helperText={touched.percent_discount && errors.percent_discount}
                  >
                    {discount.map((option, idx) => (
                      <MenuItem key={`option-${idx}`} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    required
                    className="mx-2"
                    name="editedSubscriptionOption.sproduct_label"
                    label={'Option Label'}
                    value={values.editedSubscriptionOption.sproduct_label}
                    variant="outlined"

                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.editedSubscriptionOption?.sproduct_label && Boolean(errors.editedSubscriptionOption?.sproduct_label)}
                    helperText={touched.editedSubscriptionOption?.sproduct_label && errors.editedSubscriptionOption?.sproduct_label}
                  />
                </CardContent>
                <DialogActions className="m-4">
                  <Button
                    className="text-muted"
                    onClick={() => setEditableSubscription(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!(isValid && dirty)}
                    color="primary"
                    variant="contained"
                  >
                    Edit Option
                  </Button>
                </DialogActions>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubscriptionModal;
